import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ChevronLeft,
  ChevronRight,
  RotateCw,
  RotateCcw,
  Download,
  X,
  Loader2,
  AlertCircle,
  FileText,
} from 'lucide-react';
import { adminAPI } from '../utils/api';

// Cache partagé avec DocumentPreview : chaque pièce n'est téléchargée qu'une fois,
// que ce soit pour la vignette ou pour le visualiseur.
const previewCache = new Map();

export const fetchFilePreview = (dossierId, fileId) => {
  const key = `${dossierId}:${fileId}`;
  if (!previewCache.has(key)) {
    const promise = adminAPI
      .getFilePreview(dossierId, fileId)
      .then((response) => response.data)
      .catch((err) => {
        previewCache.delete(key); // permet de réessayer à la prochaine ouverture
        throw err;
      });
    previewCache.set(key, promise);
  }
  return previewCache.get(key);
};

const base64ToBlobUrl = (base64, contentType) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: contentType }));
};

// Vignette carrée du bandeau de défilement : charge sa propre pièce (via le cache
// partagé) pour que l'admin identifie la photo au premier coup d'œil, sans attendre
// l'ouverture en grand.
const FilmstripThumb = ({ dossierId, fileId, rotation = 0 }) => {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchFilePreview(dossierId, fileId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [dossierId, fileId]);

  const contentType = preview?.content_type || '';
  const data = preview?.content || preview?.data;
  const isImage = contentType.startsWith('image/') && contentType !== 'image/heic';

  if (isImage && data) {
    return (
      <img
        src={`data:${contentType};base64,${data}`}
        alt=""
        className="w-10 h-10 object-cover rounded shrink-0"
        style={{ transform: `rotate(${rotation}deg)` }}
        draggable={false}
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center shrink-0">
      <FileText size={18} className="text-white/50" />
    </div>
  );
};

const DocumentViewer = ({ dossierId, files, open, initialIndex = 0, onClose, rotations, onRotate }) => {
  const [index, setIndex] = useState(initialIndex);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  // Ref en state (pas useRef) : Radix monte le contenu du dialog dans un Portal un
  // cycle de commit après que `open` passe à true, donc un effect gardé par
  // [open, loading] peut s'exécuter alors que stageRef.current est encore null et ne
  // se redéclenche jamais (le document reste alors affiché en 0×0, invisible). En
  // gardant le noeud DOM lui-même en state, l'effet de mesure se redéclenche dès
  // qu'il apparaît réellement.
  const [stageEl, setStageEl] = useState(null);

  const count = files?.length || 0;
  const file = files?.[index];
  const rotation = rotations?.[file?.file_id] || 0;

  useEffect(() => {
    if (open) setIndex(Math.min(initialIndex, Math.max(count - 1, 0)));
  }, [open, initialIndex, count]);

  useEffect(() => {
    if (!open || !file?.file_id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);
    fetchFilePreview(dossierId, file.file_id)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || 'Impossible de charger le fichier');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, dossierId, file?.file_id]);

  const contentType = preview?.content_type || '';
  const isImage = contentType.startsWith('image/') && contentType !== 'image/heic';
  const isPDF = contentType === 'application/pdf';
  const previewData = preview?.content || preview?.data;

  const blobUrl = useMemo(
    () => (previewData ? base64ToBlobUrl(previewData, contentType) : null),
    [previewData, contentType]
  );
  useEffect(() => () => blobUrl && URL.revokeObjectURL(blobUrl), [blobUrl]);

  // Mesure de la zone d'affichage : nécessaire pour qu'un document pivoté de 90°
  // tienne dans l'écran (on échange largeur et hauteur avant la rotation).
  useLayoutEffect(() => {
    if (!stageEl) return;
    const update = () => setStageSize({ width: stageEl.clientWidth, height: stageEl.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stageEl);
    return () => observer.disconnect();
  }, [stageEl]);

  const goTo = useCallback(
    (next) => {
      if (count === 0) return;
      setIndex((next + count) % count);
    },
    [count]
  );

  const rotate = useCallback(
    (delta) => {
      if (!file?.file_id || !onRotate) return;
      onRotate(file.file_id, ((rotation + delta + 360) % 360));
    },
    [file?.file_id, rotation, onRotate]
  );

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'ArrowRight') goTo(index + 1);
      else if (e.key === 'ArrowLeft') goTo(index - 1);
      else if (e.key === 'r') rotate(90);
      else if (e.key === 'R') rotate(-90);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, index, goTo, rotate]);

  const quarterTurn = rotation % 180 !== 0;
  const boxStyle = {
    width: quarterTurn ? stageSize.height : stageSize.width,
    height: quarterTurn ? stageSize.width : stageSize.height,
    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex flex-col text-white focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Barre d'outils */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-black/60">
            <DialogPrimitive.Title className="text-sm font-medium truncate" title={file?.filename}>
              <span className="text-white/60 mr-2">
                {index + 1} / {count}
              </span>
              {file?.filename}
            </DialogPrimitive.Title>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => rotate(-90)}
                className="p-2 rounded hover:bg-white/15"
                title="Pivoter à gauche (Maj+R)"
                aria-label="Pivoter à gauche"
              >
                <RotateCcw size={18} />
              </button>
              <button
                type="button"
                onClick={() => rotate(90)}
                className="p-2 rounded hover:bg-white/15"
                title="Pivoter à droite (R)"
                aria-label="Pivoter à droite"
              >
                <RotateCw size={18} />
              </button>
              {blobUrl && (
                <a
                  href={blobUrl}
                  download={file?.filename || 'document'}
                  className="p-2 rounded hover:bg-white/15"
                  title="Télécharger"
                  aria-label="Télécharger"
                >
                  <Download size={18} />
                </a>
              )}
              <DialogPrimitive.Close
                className="p-2 rounded hover:bg-white/15 ml-2"
                title="Fermer (Échap)"
                aria-label="Fermer"
              >
                <X size={20} />
              </DialogPrimitive.Close>
            </div>
          </div>

          {/* Document courant */}
          <div className="relative flex-1 min-h-0">
            <div ref={setStageEl} className="absolute inset-4 sm:inset-x-16 overflow-hidden">
              {loading && (
                <div className="h-full flex flex-col items-center justify-center text-white/70">
                  <Loader2 className="animate-spin mb-2" size={32} />
                  Chargement…
                </div>
              )}

              {!loading && error && (
                <div className="h-full flex flex-col items-center justify-center text-red-300">
                  <AlertCircle className="mb-2" size={32} />
                  {error}
                </div>
              )}

              {!loading && !error && blobUrl && (isImage || isPDF) && (
                <div
                  className="absolute left-1/2 top-1/2 transition-transform duration-200"
                  style={boxStyle}
                >
                  {isImage ? (
                    <img
                      src={blobUrl}
                      alt={file?.filename}
                      className="w-full h-full object-contain select-none"
                      draggable={false}
                    />
                  ) : (
                    <iframe
                      src={blobUrl}
                      title={file?.filename}
                      className="w-full h-full bg-white rounded"
                    />
                  )}
                </div>
              )}

              {!loading && !error && preview && !isImage && !isPDF && (
                <div className="h-full flex flex-col items-center justify-center text-white/70">
                  <FileText className="mb-2" size={48} />
                  Aperçu indisponible ({contentType || 'type inconnu'}) — utilisez le bouton Télécharger.
                </div>
              )}
            </div>

            {count > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => goTo(index - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/80"
                  aria-label="Pièce précédente"
                >
                  <ChevronLeft size={28} />
                </button>
                <button
                  type="button"
                  onClick={() => goTo(index + 1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/80"
                  aria-label="Pièce suivante"
                >
                  <ChevronRight size={28} />
                </button>
              </>
            )}
          </div>

          {/* Bandeau carrousel : vignette + nom, pour sauter d'une pièce à l'autre d'un coup d'œil */}
          {count > 1 && (
            <div className="flex gap-2 overflow-x-auto px-4 py-3 bg-black/60">
              {files.map((f, i) => (
                <button
                  key={f.file_id || i}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={`shrink-0 flex items-center gap-2 max-w-[200px] pl-1.5 pr-3 py-1.5 rounded text-xs truncate border transition-colors ${
                    i === index
                      ? 'border-white bg-white/20'
                      : 'border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                  title={f.filename}
                >
                  <FilmstripThumb
                    dossierId={dossierId}
                    fileId={f.file_id}
                    rotation={rotations?.[f.file_id] || 0}
                  />
                  <span className="truncate">{i + 1}. {f.filename}</span>
                </button>
              ))}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default DocumentViewer;
